import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Posts.css"; // Import the CSS for styling
import { PostModel } from "../../../Hung/PostModel";
import { useParams } from "react-router-dom";
import { useExtractColors } from "react-extract-colors";
import { extractColors } from "extract-colors";
import { FinalColor } from "extract-colors/lib/types/Color";
import { FaCompass } from "react-icons/fa";
import { TbAppsFilled } from "react-icons/tb";
import { RiImageCircleAiFill } from "react-icons/ri";
import { ImSearch } from "react-icons/im";
import { RiGroup2Fill } from "react-icons/ri";
import { FaPaperclip } from "react-icons/fa";

interface Post {
  id: number;
  title: string;
  description: string;
  image: string;
  author: string;
  likes: number;
  saves: number;
  tags: string[];
  height: number;
  profileImage: string;
}


interface UserPostsResponse {
    success: boolean;
    data: string[];
    error?: string;
}

export interface BatchPostResponse {
  success: boolean;
  data: PostModel[];
  error?: string;
}

const Posts = ({ postIds, backgroundColor, isDiscovery, theme }: { postIds?: string[], backgroundColor?: string, isDiscovery?: boolean, theme?: 'dark' | 'light' | undefined }) => {
  const params = useParams<{ theme: string; backgroundColor: string; param: string }>();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredPost, setHoveredPost] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set());
  const [savedPosts, setSavedPosts] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const masonryRef = useRef<HTMLDivElement>(null);

  // Modal state
  const [selectedPost, setSelectedPost] = useState<PostModel | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Generate comprehensive dummy data
  useEffect(() => {
    const generatePosts = (): Post[] => {
      const categories = {
        "Home & Decor": {
          titles: [
            "Cozy Living Room Makeover",
            "Modern Kitchen Design Ideas",
            "Scandinavian Bedroom Inspiration",
            "Bohemian Apartment Tour",
            "Minimalist Home Office",
            "Rustic Farmhouse Kitchen",
            "Small Space Storage Solutions",
            "Elegant Bathroom Renovation",
            "Outdoor Patio Paradise",
            "Vintage Furniture Finds"
          ],
          tags: ["home", "decor", "interior", "design", "diy"]
        },
        "Fashion & Style": {
          titles: [
            "Summer Fashion Trends 2025",
            "Capsule Wardrobe Essentials",
            "Street Style Inspiration",
            "Vintage Fashion Revival",
            "Sustainable Fashion Brands",
            "Winter Coat Collection",
            "Jewelry Layering Guide",
            "Casual Friday Outfits",
            "Evening Wear Elegance",
            "Shoe Collection Goals"
          ],
          tags: ["fashion", "style", "outfit", "trends", "clothing"]
        },
        "Food & Recipes": {
          titles: [
            "Homemade Sourdough Bread",
            "Healthy Smoothie Bowls",
            "Pasta Night Perfection",
            "Artisan Coffee Guide",
            "Farm-to-Table Cooking",
            "Dessert Masterpieces",
            "Quick Weeknight Dinners",
            "Brunch Recipe Collection",
            "International Cuisine Tour",
            "Plant-Based Meal Prep"
          ],
          tags: ["food", "recipe", "cooking", "baking", "healthy"]
        },
        "Travel & Adventure": {
          titles: [
            "Hidden Gems in Europe",
            "Road Trip Photography",
            "Mountain Hiking Adventures",
            "City Break Essentials",
            "Beach Paradise Destinations",
            "Cultural Heritage Sites",
            "Solo Travel Tips",
            "Adventure Photography",
            "Local Food Discoveries",
            "Luxury Travel Experiences"
          ],
          tags: ["travel", "adventure", "photography", "explore", "wanderlust"]
        },
        "Art & Creativity": {
          titles: [
            "Watercolor Painting Techniques",
            "Digital Art Inspiration",
            "Pottery Workshop Creations",
            "Photography Masterclass",
            "Creative Writing Prompts",
            "Abstract Art Collection",
            "Handmade Jewelry Design",
            "Calligraphy Practice",
            "Mixed Media Artwork",
            "Creative Process Behind Art",
          ],
          tags: ["art", "creative", "painting", "photography", "inspiration"]
        }
      };

      const authors = [
        { name: "Sarah Mitchell", avatar: "https://i.pravatar.cc/150?img=1" },
        { name: "Emma Rodriguez", avatar: "https://i.pravatar.cc/150?img=2" },
        { name: "Olivia Chen", avatar: "https://i.pravatar.cc/150?img=3" },
        { name: "Maya Patel", avatar: "https://i.pravatar.cc/150?img=4" },
        { name: "Sophie Turner", avatar: "https://i.pravatar.cc/150?img=5" },
        { name: "Isabella Davis", avatar: "https://i.pravatar.cc/150?img=6" },
        { name: "Zoe Williams", avatar: "https://i.pravatar.cc/150?img=7" },
        { name: "Luna Thompson", avatar: "https://i.pravatar.cc/150?img=8" },
        { name: "Aria Johnson", avatar: "https://i.pravatar.cc/150?img=9" },
        { name: "Chloe Anderson", avatar: "https://i.pravatar.cc/150?img=10" }
      ];

      const descriptions = [
        "Discover amazing ideas and inspiration for your next project",
        "Transform your space with these beautiful design concepts",
        "Get inspired by this stunning collection of ideas",
        "Perfect inspiration for your creative journey",
        "Beautiful designs that will spark your imagination",
        "Trending ideas you'll absolutely love and want to try",
        "Creative solutions for modern living and style",
        "Timeless inspiration that never goes out of style",
        "Fresh perspectives on classic design themes",
        "Innovative approaches to everyday creativity"
      ];

      const allPosts: Post[] = [];
      let postId = 1;

      Object.entries(categories).forEach(([category, data]) => {
        data.titles.forEach((title, index) => {
          const author = authors[index % authors.length];
          const height = 280 + Math.floor(Math.random() * 450); // 280-730px
          
          allPosts.push({
            id: postId++,
            title,
            description: descriptions[index % descriptions.length],
            image: `https://picsum.photos/400/${height}?random=${postId}`,
            author: author.name,
            profileImage: author.avatar,
            likes: Math.floor(Math.random() * 2500) + 50,
            saves: Math.floor(Math.random() * 800) + 20,
            tags: [...data.tags, category.toLowerCase().replace(/ & /g, '-')],
            height
          });
        });
      });

      // Shuffle the posts for random order
      return allPosts.sort(() => Math.random() - 0.5);
    };

    // Simulate loading time
    setPosts(generatePosts());
    setLoading(false);
    // setTimeout(() => {
    //   setPosts(generatePosts());
    //   setLoading(false);
    // }, 1200);
    fetchPosts();
  }, []);

  // Filter posts based on search term
  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
    post.author.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePostClick = (post: PostModel) => {
    setSelectedPost(post);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPost(null);
  };

  const handlePostSubmit = () => {
    // Refresh posts after successful post creation
    fetchPosts();
  };

  const handleLike = (postId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setLikedPosts(prev => {
      const newLiked = new Set(prev);
      if (newLiked.has(postId)) {
        newLiked.delete(postId);
        // Decrease like count - not applicable for PostModel, just toggle
      } else {
        newLiked.add(postId);
        // Increase like count - not applicable for PostModel, just toggle
      }
      return newLiked;
    });
  };

  const handleSave = (postId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSavedPosts(prev => {
      const newSaved = new Set(prev);
      if (newSaved.has(postId)) {
        newSaved.delete(postId);
        // Remove from saved - just toggle for now
      } else {
        newSaved.add(postId);
        // Add to saved - just toggle for now
      }
      return newSaved;
    });
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleTagClick = (tag: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSearchTerm(tag);
  };

  const handleCollageClick = () => {
    // Add collage functionality here
  };

  const handleSearchClick = () => {
    // Add search modal/view logic here
  };

  const handleBookmarkClick = () => {
    // Add bookmark functionality here
  };

  const handleNotificationClick = () => {
    // Add notification view logic here
  };

  const handleProfileClick = () => {
    // Add profile view logic here
  };

  const handleMenuClick = () => {
    // Add menu toggle logic here
  };

  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState<string | null>(null);
  const [recommendedPosts, setRecommendedPosts] = useState<PostModel[]>([]);
  const fetchPosts = async (offsetVal: number = 0) => {
      try {
          fetchPostsBatch( postIds || [] );
          // fetchPostsBatch(["17f98186-47e6-4c3b-b53e-c582c0396ecf","1a6806a5-955f-46cd-ab01-ad47550a1d38","957ca34d-a923-4f7a-89c2-5b11e3cc569a","d3429414-3a05-4569-bb38-793865c80d04","08e00a71-9462-4ba0-95d3-9bc36079a6b8","e2908ece-b975-460f-86e6-f033f71eda44","ff5159a7-c941-4258-b596-c00f5d8c90b9","0ed32b22-47a9-446d-ae50-7d20064d57bd","c507faae-6ca9-4374-bc5c-3cc7c8abc89e","b937508e-d28a-4942-9bae-0a4369f17802","5b18ca1e-c28f-4ebe-9c0d-72dd9e125516","f226d927-bbae-4bf1-8c8a-3a4969d30f3a","e0eef488-c39a-4da0-9c67-cd054ba20333","b7778dfe-9745-4c8d-9d18-bae6da082bcc","61f72bc9-c3e1-4da6-acbe-c940865dd1b8","612a3dba-8e38-41d8-8724-c4d1143809fe","43c203eb-b781-413f-b5ca-a4c8f5bb0c95","0d1f100a-19ac-412c-8a25-9e783692fad7","d7da068d-9dea-41b2-8c15-3fe7519ae8ba","1h6K7G7sErkhq6jdCy1PsARKCQCm4oWjbBQyaVhayQOZ3jh84DAUPfo1ID7m9rc4nVlWnAkEggyQf1R2YMSg1XimyGvpE2LNkCXU","ec457528-d09f-4c9e-9e98-3ff1ba16296a","a45ecb30-7510-4116-ac0a-cd28a72742af","192c8bca-bc81-42da-aac0-d4ae4b5a6afa","268a88d9-320e-4de5-99b4-b95a86791d56","d26076db-6812-46f5-9068-da5955eb7dad","ZxCtlK0xdKqa71cUGFBtD7jA7dEPXILMloFmugT9QeCbXw3YrRV4ZjvWchCupDBoSHLhI0wMN8xvyjOSNXW6APU4L5qPrJSE2QOB","c645900b-c83e-4c40-bdad-b22b890c3539","9a2c6ffa-9ef7-4ee8-881c-bf682e98956e","d008da7e-d1ce-4819-a5cb-560e57ca7d53","6d101566-263d-4ee4-9a97-877b57fd1545","6f346176-4221-40d9-a45f-1389144e7063","bpSAjLgiAdk6YExbqctPTvN54VTR0eTavcvnu53VUaCrUsXZMfyUfANcZiLTAzAA47zkM3jr2xAYtkCSlsvuABZmMVkUjMyzxmII","wiki-1627-1751758279551","10bc02df-8cc9-4e99-afc1-1713a8dc0feb","0e99cc71-ad78-4940-80b4-c89e89fc92af","7e7bcf74-a4df-4213-b767-438b49617ecd","ac52554e-dd7d-4226-95d2-1c35c8139f16","mG7SHgKWpEakL2wph6DsjWGQwjceL4krzdRcAMNiNxh6XCjx9IoftJpp1CWjShvmtQanmMNC0eSnQ612xpO8VG56GzWDTSofunot","RnqEL6SBOXBkhX4eCuX0VizSQlCoE2hXgpcSR0Q5pG3PMJXk1eRlhsL2D7bqOK1Dorx4wKCfCz05jSVtWYAIdbWFBESZ0skcewkV","aa1d5c3a-75f2-4175-91b6-7ee1ecfe4ac3","jsHpYxBxBIf5MWJ6SenHPBCXE43D2Ph8eANdI2GiXuFiDCPUP0NDmuQekmrO5F2DdWXNpj5BdTg0xKdypE0tMeyY2ijoWvLMT7DH","f89eedea-e792-413b-8a06-46542ac2f83e","a6c9eb91-48d7-41a2-9129-1d372a13f13d","cfe611fd-e867-4e52-be06-28b0d7fe8eaa","1281650c-fc08-4b0c-83fa-3cec6b09bb43","bcb65c81-d95d-403d-a044-729d1fdfdf7b","20aeb2a1-89d6-4ce7-ab3d-720d697108fd","51e5fc3d-2b5c-49b0-8a83-edc6b03860f5","d0ca43a0-d05a-49f2-8c84-a9cfce5a7d51","9aac06ba-7208-4555-a643-8f375e053581","8ade5f35-8748-4883-9e6e-d799aca9ab75","e623072e-bee2-425f-9837-f12ba07611be","04e14cef-97e3-45ff-a4d1-360b253022cb","aaaf4330-3e17-43c7-8f6d-21d87e0dd9f8","17d58782-0a39-4599-85cf-c565f2159b64","85909f75-9f0d-42f4-8d46-fa102cac7cda","a01a9ebf-7671-49d1-b930-ad7ea112bd0c","mV3iJt5ncCHXyDDfsKrHejxAKhTrcrUb1zvRxZSBAKHRSuIwuv5kWZYAU9gYFvJKv2wKX17fZkHTQqeuH0wijqjBQh0XLUx6xo0q","wiki-2393-1751692104412","5e53c380-c98c-461b-99e3-490f812454f6","wiki-2362-1751690737889","wiki-580-1751691080093","70256bb8-f7e7-4e68-9937-b473a49a5666","X6IWPDQGRpnnFmtUqCjuN0rHLV6dTrPMaG0rw6O67sWZw6PjyY9JugqIHKUtSCOoFNizAgDbIQEDuQrErUaZAlEV7wMNlOOXenM4","wiki-1634-1751691111130","wiki-3392-1751690125405","5ddb5ed5-c261-448e-b862-13e60cd23a0b","bdc0e58d-58e8-4a43-a08c-e28fc055d56f","1352e1e4-7535-414a-9044-415cd280788e","291adb4d-ac1c-4dc7-9881-796f9a2fb739","107431b8-9acd-4400-98d3-7116c6bdcc32","12c710a5-403a-422f-86bb-73ce3adb4baa","b015f4e9-4a5b-4380-9959-a2725acda5c0","877702ee-8e46-46b4-8ed9-bc913d9da2b8","d3b9fecd-177b-40b4-8c04-f1f0df12471f","395217c1-1498-41db-ba9c-05be7986d38b","da980969-944c-499d-8d5c-a781ae7bbd7f","f51523a6-5118-47ec-962f-350bee80d44e","0bfb72f9-f5ca-45c5-9ef7-787f71a16c98","kmMOTepnmItS7bO3X7MeLw28I2teAKUiQeaNuHtCvRwYL8IOIync3vx4IqpKQFDzYSehnutBTGjZqoaDZSFb200I5eIocOnFQv7H","79a29449-dc4b-49f3-8618-75f433def0d8"]);        
      } catch (error) {
          setPostsError('Failed to load posts');
      } finally {
          setPostsLoading(false);
      }
  };


  const fetchPostsBatch = useCallback(async (postIds: string[]) => {
    if (postIds.length === 0) return;

    try {
      const response = await fetch(
        `${'https://api-d2cime47qa-uc.a.run.app'}/api/posts/batch`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ postIds })
        }
      );

      const result: BatchPostResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch posts batch');
      }

      // Process and cache the fetched posts
      setRecommendedPosts(result.data.map(post => post))

    } catch (err) {
      console.error('Error fetching posts batch:', err);
      setPostsError('Failed to load posts');
      return null;
    }
  }, []);


  const fetchPostsBatchByPostIds = async (postIds: string[]) => {
    if (postIds.length === 0) return;

    try {
      const response = await fetch(
        `${'https://api-d2cime47qa-uc.a.run.app'}/api/posts/batch`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ postIds })
        }
      );

      const result: BatchPostResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch posts batch');
      }

      // Process and cache the fetched posts
      setRecommendedPosts(result.data.map(post => post))

    } catch (err) {
      console.error('Error fetching posts batch:', err);
      setPostsError('Failed to load posts');
      return null;
    }
  }

  const fetchPostsBatchByColor = async (embeddings: number[]): Promise<string[]> => {
    if (embeddings.length === 0) return [];

    try {
      const response = await fetch(
        `${'https://api-d2cime47qa-uc.a.run.app'}/api/color-search`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ embeddings })
        }
      );

      const result: BatchPostResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch posts batch');
      }

      if (result.data.length > 0) {
        const postIds = result.data.map(post => post.postId);
        // await fetchPostsBatchByPostIds(postIds);
        return postIds;
      }

      return [];

    } catch (err) {
      console.error('Error fetching posts batch:', err);
      setPostsError('Failed to load posts');
      return [];
    }
  }


  return (
    <div className="Posts-mobile-app pma" ref={containerRef} style={{ backgroundColor: `${backgroundColor}`, background: `${params.backgroundColor} !important`,}}>
      <div className="posts-masonry" ref={masonryRef} style={{  overflow: "none !important",}}>
        {recommendedPosts.reverse().map((post, index) => (
          <PostItem 
            key={post.postId}
            post={post}
            index={index}
            containerRef={containerRef}
            fetchPostsBatchByColor={fetchPostsBatchByColor}
            isDiscovery={isDiscovery}
            backgroundColor={backgroundColor}
            theme={theme}
          />
        ))}
      </div>
    </div>
  );
};

export default Posts;


const PostItem = (
  {
    post,
    index,
    containerRef, 
    fetchPostsBatchByColor,
    isDiscovery = false,
    theme,
    backgroundColor,
  }: 
  {
    post: PostModel;
    index: number;
    containerRef: React.RefObject<HTMLDivElement>;
    fetchPostsBatchByColor: (embeddings: number[]) => Promise<string[]>;
    isDiscovery?: boolean;
    theme?: 'dark' | 'light';
    backgroundColor?: string, 
  }
) => {
  const image = (post.mediaUrls && post.mediaUrls[0]) || "https://picsum.photos/400/300?random=1";// post.tinyFirstImageURL || (post.mediaUrls && post.mediaUrls[0]) || "https://picsum.photos/400/300?random=1";
  const [ colors, setColors ] = useState<FinalColor[]>([]);
  const [ averageHSV, setAverageHSV ] = useState<{ hue: number, saturation: number, lightness: number, red: number, green: number, blue: number }>({  hue: 0, saturation: 0, lightness: 0, red: 0, green: 0, blue: 0});

  function calculateAverageColor(colors: FinalColor[]): { hue: number, saturation: number, lightness: number, red: number, green: number, blue: number } {
    let sumX = 0;
    let sumY = 0;
    let sumSaturation = 0;
    let sumLightness = 0;
    let sumRed = 0;
    let sumGreen = 0;
    let sumBlue = 0;

    colors.forEach(color => {
        // For Hue: Convert hue from 0-1 range (assuming your color.hue is 0-1) to radians (0 to 2*PI)
        // If your color.hue is already 0-360 degrees, just multiply by (Math.PI / 180)
        const hueRadians = color.hue * 2 * Math.PI; 

        sumX += Math.cos(hueRadians);
        sumY += Math.sin(hueRadians);

        sumSaturation += color.saturation; // Assuming saturation is 0-1
        sumLightness += color.lightness;   // Assuming lightness is 0-1
        sumRed += color.red;
        sumGreen += color.green;
        sumBlue += color.blue;
    });

    const averageHSV = { hue: 0, saturation: 0, lightness: 0, red: 0, green: 0, blue: 0 };

    if (colors.length > 0) {
        // Calculate average hue
        const averageHueRadians = Math.atan2(sumY / colors.length, sumX / colors.length);
        // Convert average hue back to 0-1 range (or 0-360 if preferred)
        let averageHue = averageHueRadians / (2 * Math.PI); 
        // Ensure hue is positive
        averageHue = (averageHue + 1) % 1; // For 0-1 range

        averageHSV.hue = Math.round(averageHue * 360);
        averageHSV.saturation = Math.round(sumSaturation / colors.length * 100);
        averageHSV.lightness = Math.round(sumLightness / colors.length * 100);
        averageHSV.red = Math.round(sumRed / colors.length);
        averageHSV.green = Math.round(sumGreen / colors.length);
        averageHSV.blue = Math.round(sumBlue / colors.length);
    }

    return averageHSV;
  }

  const isLongPress = useRef(false);
  const longPressTimeout = useRef<NodeJS.Timeout | null>(null);
  const [ isPopUpVisible, setIsPopUpVisible ] = useState(false);
  const [ popUpPosition, setPopUpPosition ] = useState<{ x: number, y: number } | null>(null);

  useEffect(() => {
    if (image) {
      const getColors = async () => {
        extractColors(image).then((colors) => {
          const colorsHueConversion = colors.map(color => {
            return {
              ...color,
              hue: color.hue * 360, // Convert hue from 0-1 range to 0-360
              saturation: color.saturation * 100, // Convert saturation from 0-1 range to 0-100
              lightness: color.lightness * 100, // Convert lightness from 0-1 range to 0-100
            };
          });
          setColors(colorsHueConversion);
          const _averageHSV = calculateAverageColor(colors);
          setAverageHSV(_averageHSV);
        }).catch(console.error);
      }
      getColors();
    }
  }, []);
  return (
          
          <>
          <div key={post.postId} className="post-pin" style={{...( index === 0 ? {marginTop: "10px"} : {} ) }}>
            <div className="pin-image-container">
              <span style={{
                  pointerEvents: 'none', 
                  width: '100%',
                  aspectRatio: post.meta?.imageMetaData[0]?.aspectRatio ? `${post.meta.imageMetaData[0].aspectRatio}` : 'auto',
                  backgroundColor: post.meta?.imageMetaData[0] ? post.meta?.imageMetaData[0].averageColor : 'rgba(113, 113, 113, 1)',
                }}
                onClick={() => { 
                  // Conditional if not longpress do: " console.log({...post, imageUrl: image, "____is_post_click" : true});"
                  if (!isLongPress.current) {
                    console.log({...post, imageUrl: image, "____is_post_click" : true});
                  }
                }}
              >
                <img
                  src={image}
                  alt={post.title}
                  className="pin-image"
                  style={{ 
                    pointerEvents: 'none', 
                    width: '100%',
                    aspectRatio: post.meta?.imageMetaData[0]?.aspectRatio ? `${post.meta.imageMetaData[0].aspectRatio}` : 'auto',
                    backgroundColor: post.meta?.imageMetaData[0] ? post.meta?.imageMetaData[0].averageColor : 'rgba(113, 113, 113, 1)',
                  }}
                  loading="lazy"
                  onLoadStart={(e) => {
                  }}
                  onLoad={(e) => {
                  }}
                />
              </span>
              {/* Floating Action Button */}
              <div className="floating-action-buttons">
                {/* Add your button content here */}
               { post.categories.filter((_, index) => (index < 5 && _.trim().length > 0)).length > 0 && 
                  <button 
                    className="action-button"
                    onClick={() => {  console.log({...post, imageUrl: image, "____is_post_colony_search_click" : true}); }}
                    style={{ background: backgroundColor ? backgroundColor : theme  === 'dark' ? 'black' : '#fff', color: theme  === 'dark' ? 'rgba(209, 209, 209, 1)' : 'rgba(56, 56, 56, 1)' }}>
                    <RiGroup2Fill style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(calc(-50% + 1px), calc(-50% - 7px))', fontSize: '10px', zIndex: 2 }} />
                    <ImSearch style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '12px', zIndex: 1,}} />
                  </button>
                }

                <button 
                  className="action-button"
                  onClick={() => {  console.log({...post, imageUrl: image, "____is_post_image_search_click" : true}); }}
                  style={{ background: backgroundColor ? backgroundColor : theme  === 'dark' ? 'black' : '#fff', color: theme  === 'dark' ? 'rgba(209, 209, 209, 1)' : 'rgba(56, 56, 56, 1)' }}>
                  <RiImageCircleAiFill style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(calc(-50% + 1px), calc(-50% - 7px))', fontSize: '10px', zIndex: 2 }} />
                  <ImSearch style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '12px', zIndex: 1 }} />
                </button>
                <button 
                  className="action-button"
                  onClick={() => {  console.log({...post, imageUrl: image, "____is_add_clip_post_colony_click" : true}); }}
                  style={{ backgroundColor: backgroundColor ? backgroundColor : theme  === 'dark' ? 'black' : '#fff', color: theme  === 'dark' ? 'rgba(209, 209, 209, 1)' : 'rgba(56, 56, 56, 1)' }}>
                  <FaPaperclip style={{ fontSize: '12px', }} />
                </button>
                <button 
                  className="action-button"
                  onClick={() => {  console.log({...post, imageUrl: image, "____is_add_post_to_content_colony_click" : true}); }}
                  style={{ backgroundColor: backgroundColor ? backgroundColor : theme  === 'dark' ? 'black' : '#fff', color: theme  === 'dark' ? 'rgba(209, 209, 209, 1)' : 'rgba(56, 56, 56, 1)' }}>
                  <TbAppsFilled style={{ fontSize: '12px', }} />
                </button>
              </div>
              
            </div>
            <div 
              className="color-palette-scroll-container"
              style={{ 
                position: 'relative',
                width: '100%',
                height: 'fit-content',
                display: 'flex',
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE/Edge
                // justifyContent: 'center',
                // alignItems: 'center',
              }}
            >
              <div className="color-palette" style={{ 
                display: 'flex', 
                flexWrap: 'nowrap',
                justifyContent: 'flex-start', 
                alignItems: 'center', 
                alignSelf: 'center',
                width: 'max-content',
                gap: '4px',
                padding: '4px 0'
              }}>
                {colors.map((color, index) => (
                    <span 
                      key={index}
                      style={{ 
                        color: `rgb(${color.red}, ${color.green}, ${color.blue})`,
                        border: ` 1px dashed rgb(${color.red}, ${color.green}, ${color.blue})`,
                        fontSize: '10px',
                        fontWeight: '600',
                        borderRadius: '8px',
                        margin: '0 2px',
                        padding: '4px 8px',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        cursor: 'pointer'
                      }}
                      onClick={async () => {
                        const colorPosts = await fetchPostsBatchByColor([color.hue, color.saturation, color.lightness])
                        const object = {
                          colorHSV: {
                            h: color.hue,
                            s: color.saturation,  
                            v: color.lightness
                          },
                          hex: color.hex,
                          colorRGB: {
                            r: color.red,
                            g: color.green,
                            b: color.blue
                          },
                          postIds: colorPosts,
                        };
                        const objString = JSON.stringify(object);
                        console.log(`_____color_clicked___${objString}`);
                      }}
                    >
                      {`${color.hex}`}
                    </span>

                  // <div 
                  //   key={index} 
                  //   className="color-swatch" 
                  //   style={{ 
                  //     backgroundColor: `rgb(${color.red}, ${color.green}, ${color.blue})`, 
                  //     color: `rgb(${color.red}, ${color.green}, ${color.blue})`, 
                  //     margin: '4px',
                  //     border: `1.5px solid rgb(${averageHSV.red}, ${averageHSV.green}, ${averageHSV.blue})`,
                  //     display: 'flex',
                  //     flexWrap: 'wrap',
                  //     justifyContent: 'center',
                  //     alignItems: 'center',
                  //   }} 
                  //   onClick={async () => {
                  //       const colorPosts = await fetchPostsBatchByColor([color.hue, color.saturation, color.lightness])
                  //       const object = {
                  //         colorHSV: {
                  //           h: color.hue,
                  //           s: color.saturation,  
                  //           v: color.lightness
                  //         },
                  //         hex: color.hex,
                  //         colorRGB: {
                  //           r: color.red,
                  //           g: color.green,
                  //           b: color.blue
                  //         },
                  //         postIds: colorPosts,
                  //       };
                  //       const objString = JSON.stringify(object);
                  //       console.log(`_____color_clicked___${objString}`);
                  //     }
                  //   }
                  // >
                  //   {/* <div style={{ width: '100%', height: '100%', backgroundColor: `${color.hex}`,}}/> */}
                  //  {/* {`rgb(${color.red}, ${color.green}, ${color.blue})`} */}
                  //  {`${color.hex}`}
                  //   {/* <FaCompass style={{ 
                  //     position: 'relative',
                  //     fontSize: '20px', 
                  //     // marginLeft: '4px', 
                  //     display: 'inline-block', 
                  //     // verticalAlign: 'middle',
                  //     // color: `rgb(${averageHSV.red}, ${averageHSV.green}, ${averageHSV.blue})`,
                  //     color: `rgb(${color.red}, ${color.green}, ${color.blue})`, 
                  //   }}/> */}
                  // </div>
                ))}
              </div>
            </div>
            <div 
              className="categories-scroll-container"
              style={{
                position: 'relative',
                width: '100%',
                height: 'fit-content',
                display: 'flex',
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE/Edge
              }}
            >
              { isDiscovery && post.categories && post.categories.length > 0 && (
                <div 
                  className="post-categories" 
                  style={{ 
                    display: 'flex', 
                    flexDirection: 'row',
                    alignItems: 'center', 
                    width: 'max-content',
                    minWidth: '100%',
                    gap: '4px',
                    padding: '4px 0',
                    overflowX: 'visible',
                  }}
                >
                  {post.categories.filter((_, index) => (index < 5 && _.trim().length > 0)).map((category, index) => (
                    <span 
                      key={index} 
                      className="post-category" 
                      style={{ 
                        margin: '0 2px', 
                        padding: '6px 12px', 
                        backgroundColor: 'transparent', 
                        borderRadius: '12px', 
                        border: `1px dashed ${theme ? (theme === 'dark' ? 'white' : 'black') : `rgb(${averageHSV.red}, ${averageHSV.green}, ${averageHSV.blue})`}`,
                        fontSize: '12px', 
                        fontWeight: '500', 
                        cursor: 'pointer',
                        textAlign: 'center',
                        color: theme ? (theme === 'dark' ? 'white' : 'black') : `rgb(${averageHSV.red}, ${averageHSV.green}, ${averageHSV.blue})`,
                        opacity: '0.8',
                        transition: 'all 0.3s ease-in-out',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                       }}
                      onClick={(e) => { console.log(`_____category_clicked___${category}`); }}
                    >
                      <label style={{ cursor: 'pointer' }}>{category}</label>
                      <FaCompass style={{ fontSize: '12px', display: 'inline-block' }}/>
                    </span>
                  ))}
                </div>
              )}
            </div>
            {/* Pinterest Style Popup */}
            {isPopUpVisible && popUpPosition && (
              <div className="pinterest-popup" style={{ position: 'absolute', top: popUpPosition.y, left: popUpPosition.x }}>
                <div className="popup-content">
                  <h4>Pin this content</h4>
                  <button onClick={() => console.log('Pinned!')}>Pin</button>
                </div>
              </div>
            )}

          </div>
          </>
  );
}


const AdItem = () => {
  useEffect(() => {
    try {
      // Initialize adsbygoogle if it doesn't exist
      if (typeof window !== 'undefined') {
        (window as any).adsbygoogle = (window as any).adsbygoogle || [];
        // Push the ad configuration
        (window as any).adsbygoogle.push({});
      }
    } catch (error) {
      console.error('Error loading AdSense ad:', error);
    }
  }, []);

  return (
    <div style={{ 
      width: "calc(50vw - 20px)",
      position: "relative",
      height: "fit-content",
      minHeight: "40vh",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      overflow: "hidden",
      borderRadius: "20px",
    }}>
      {/* <!-- Vertical_PeaColony_Ad --> */}
      <ins 
        className="adsbygoogle"
        style={{ display: "block", height: '40vh', width: "calc(50vw - 20px)", }}
        data-ad-client="ca-pub-5619224767870187"
        data-ad-slot="4801692460"
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
};